import _ from 'underscore';
import { HasDateExpiredInPST, WebsiteValidationStatus } from '../helpers';

// actions
export const actions = {
  INITIALIZE: 'INITIALIZE',
  REQUEST_IN_PROGRESS: 'REQUEST_IN_PROGRESS',
  VALIDATED_WEBSITE: 'VALIDATED_WEBSITE',
  FETCHED_USER_COUPON_VALUE: 'FETCHED_USER_COUPON_VALUE',
  STORED_USER_COUPON_VALUE: 'STORED_USER_COUPON_VALUE',
  SET_COUPON_DETAILS: 'SET_COUPON_DETAILS',
  UPDATE_USER_COUPON_VALUE: 'UPDATE_USER_COUPON_VALUE',
  SET_DIALOG_COUPON_DETAILS: 'SET_DIALOG_COUPON_DETAILS',
  RESET_UPDATED_COUPON_CALLBACK: 'RESET_UPDATED_COUPON_CALLBACK',
};

// reducers
export const initialState = {
  isRequestInProgress: false,
  canValidateWebsite: false,
  canGetUserCouponValue: false,
  canGetCouponDetails: false,
  canSaveUserCouponValue: false,
  isManualCouponCode: false,
  // Coupon value refers to couponString and couponStringType (cco/ccl)
  // provided by user/url/payment-context.
  couponValue: null,
  // Coupon detail stores the coupon information fetched from server for
  // the provided couponValue
  couponDetails: null,
  // If coupon is acquisition type, we cache the details till website validation is completed before
  // setting in couponDetails.
  couponDetailsCache: null,
  websiteValidationResult: null,
  // state used to show coupon error for bad coupons
  isCouponValid: null,
  // Acquisition coupon is set to null when there is no coupon and
  // false refers to retention.
  isAcquisitionCoupon: null,
  // state used to check if coupon is expired
  isCouponExpired: null,
  // coupon value used in coupon cancel dialog, if couponValue is not empty,
  // dialogCouponValue is same as couponValue, if not, set a backup coupon for dialogCouponValue
  dialogCouponValue: null,
  // coupon details shown in coupon cancel dialog
  dialogCouponDetails: null,
  canGetBackupCouponDetails: false,
  // updated coupon callback after updating coupon value
  updatedCouponCallback: null,
  canRunUpdatedCouponCallback: false,
};

export function usePromotionalOfferReducer(state, action) {
  switch (action.type) {
    case actions.INITIALIZE: {
      return {
        ...initialState,
        canGetUserCouponValue: action.shouldInitialize,
      };
    }
    case actions.REQUEST_IN_PROGRESS: {
      return {
        ...state,
        isRequestInProgress: true,
      };
    }
    case actions.VALIDATED_WEBSITE: {
      const { websiteValidationResult } = action;
      const isWebsiteEligible = websiteValidationResult === WebsiteValidationStatus.Valid;
      const dialogCoupon = isWebsiteEligible ? state.dialogCouponDetails : null;
      return {
        ...state,
        websiteValidationResult,
        // Set coupon details only if website is eligible
        couponDetails: isWebsiteEligible ?
          state.couponDetailsCache :
          null,
        couponDetailsCache: null,
        canValidateWebsite: false,
        isRequestInProgress: false,
        canSaveUserCouponValue: true,
        dialogCouponDetails: dialogCoupon,
      };
    }
    case actions.FETCHED_USER_COUPON_VALUE: {
      // If coupon value is not empty, set it to dialogCouponValue
      const dialogCouponValue = !_.isEmpty(action.couponValue) ?
        action.couponValue : action.dialogCouponValue;
      return {
        ...state,
        couponValue: action.couponValue,
        canGetCouponDetails: !!(action.couponValue),
        canGetUserCouponValue: false,
        isRequestInProgress: false,
        dialogCouponValue,
        // When couponValue is empty and dialogCouponValue is not empty, that means the
        // dialogCouponValue use the backup coupon, set canGetBackupCouponDetails as true
        canGetBackupCouponDetails:
          _.isEmpty(action.couponValue) && !_.isEmpty(action.dialogCouponValue),
      };
    }
    case actions.SET_COUPON_DETAILS: {
      let isAcquisitionCoupon = null;
      let isCouponExpired = null;
      let shouldSkipWebsiteValidation = false;
      if (action.couponDetails) {
        // Currently as per CCMT the only way to determine if a coupon is
        // for acquisition vs retention is that all acquisition coupons start with
        // letter 'A'. We cannot use couponTypes or couponPurpose fields.
        isAcquisitionCoupon = action.couponDetails.ClassName.toLowerCase().startsWith('a');
        isCouponExpired = HasDateExpiredInPST(action.couponDetails.ExpiryDate);
        shouldSkipWebsiteValidation = action.isSkipWebsiteValidation;
      }

      // If coupon is not empty, valid and not expired, set it to dialogCouponValue
      const dialogCouponValue = (!_.isEmpty(action.couponValue) &&
        !isCouponExpired &&
        !_.isEmpty(action.couponDetails)) ?
        action.couponValue : action.dialogCouponValue;
      // If coupon detail is not empty and not expired, set it to dialogCouponDetails,
      // otherwise, set dialogCouponDetails as null, and canGetBackupCouponDetails will be true,
      // then will call api to get backupCouponDetails
      const dialogCouponDetails = !_.isEmpty(action.couponDetails) && !isCouponExpired ?
        action.couponDetails : null;
      // When dialogCouponValue is not empty and dialogCouponDetails is empty,
      // set canGetBackupCouponDetails as true to get backup coupon detail
      const canGetBackupCouponDetails = !_.isEmpty(dialogCouponValue)
        && _.isEmpty(dialogCouponDetails);

      let canValidateWebsite = false;
      if (!canGetBackupCouponDetails) {
        canValidateWebsite = !_.isEmpty(action.couponDetails) && !isCouponExpired
          && _.isNull(state.websiteValidationResult) && !shouldSkipWebsiteValidation && !!isAcquisitionCoupon;
      }
      // save coupon after website validation to avoid issue caused by call two APIs in parallel
      const canSaveUserCouponValue = !canValidateWebsite;

      return {
        ...state,
        // We don't set coupon details till website validation for acquisition coupons is completed.
        // Store the details in couponDetailsCache and set it on couponDetails only if website is
        // eligible.
        couponDetails: canValidateWebsite
          ? null : action.couponDetails,
        couponDetailsCache: action.couponDetails,
        couponValue: action.couponValue,
        canGetCouponDetails: false,
        canValidateWebsite,
        canSaveUserCouponValue,
        isRequestInProgress: false,
        isCouponValid: !_.isEmpty(action.couponValue) ? !!action.couponDetails : null,
        isAcquisitionCoupon,
        isCouponExpired,
        dialogCouponValue,
        dialogCouponDetails,
        canGetBackupCouponDetails,
      };
    }
    case actions.STORED_USER_COUPON_VALUE: {
      return {
        ...state,
        couponValue: action.value || state.couponValue,
        canSaveUserCouponValue: false,
        isRequestInProgress: false,
        canRunUpdatedCouponCallback: _.isFunction(state.updatedCouponCallback),
      };
    }
    case actions.UPDATE_USER_COUPON_VALUE: {
      if (action.couponValue &&
        state.couponValue &&
        action.couponValue.couponString === state.couponValue.couponString &&
        action.couponValue.couponStringType === state.couponValue.couponStringType) {
        return {
          ...state,
          updatedCouponCallback: action.updatedCouponCallback,
          canRunUpdatedCouponCallback: _.isFunction(action.updatedCouponCallback),
        };
      }

      return {
        ...state,
        couponValue: action.couponValue,
        isManualCouponCode: action.isManualCouponCode,
        canGetCouponDetails: !!(action.couponValue.couponString), // trigger refetch coupon
        canSaveUserCouponValue: _.isEmpty(action.couponValue.couponString), // save empty for remove
        couponDetails: null,
        isCouponValid: null,
        isAcquisitionCoupon: null,
        websiteValidationResult: null,
        isCouponExpired: null,
        updatedCouponCallback: action.updatedCouponCallback,
      };
    }
    case actions.SET_DIALOG_COUPON_DETAILS: {
      return {
        ...state,
        isRequestInProgress: false,
        canGetBackupCouponDetails: false,
        dialogCouponDetails: action.dialogCouponDetails,
        // validate website if not already done. This is needed when there is no user coupon
        // and we need to decide whether to show coupon dialog based on website validity.
        canValidateWebsite: _.isNull(state.websiteValidationResult),
      };
    }
    case actions.RESET_UPDATED_COUPON_CALLBACK: {
      return {
        ...state,
        updatedCouponCallback: null,
        canRunUpdatedCouponCallback: false,
      };
    }
    default:
      throw new Error();
  }
}
